<script setup lang="ts">
/**
 * One attachment or file: type icon (or a thumbnail), the name, a meta line
 * (size, or what the caller passes) and an optional remove button. While it
 * uploads, a thin progress bar runs along the bottom edge; on error the meta
 * line turns into the message and the chip gets a danger edge.
 *
 * With `openable` the whole chip is a button (click, Enter, Space → `open`).
 * The `actions` slot takes extra icon buttons (preview, download) before the
 * remove button — put the whole list in a flex-wrap row.
 *
 * A long name is cut in the middle: the end of the name with the extension
 * stays visible, the full name is the tooltip and the text screen readers get.
 * `selected` marks the chip that is open next to it (a preview). `deleted`
 * strikes the name through and turns opening and removing off; the hint says
 * why. `expandable` (an archive) puts the button role on the head row, which
 * toggles `v-model:expanded`; the `contents` slot opens below it inside the
 * chip and takes one sub-chip per contained file. Expandable chips never emit
 * `open`; a disabled or deleted one stays shut.
 *
 * `query` marks the search hits in the drawn name like `NxHighlight` (same
 * matching): a string or a list of strings (`minLength`, `ignoreDiacritics`),
 * or the terms of a text query. Of those, `mustNot` and disabled terms stay
 * unmarked, and `startsWith` / `endsWith` mark only at the edge of a word.
 * `fold` is `accents` by default; `spelled` uses the search's own ranges.
 * `maxTerms` caps how many terms mark (default 10). The hits are found in the
 * whole name before it is cut into head and tail, so one across the cut is
 * marked in both. The hidden full name stays plain text.
 * `look="inline"` is the same file label without the chip surface, with a
 * small icon for table rows. `#markers` holds wrapping kit badges below it.
 */
import { ChevronRightIcon } from '@blpde/nx-icons';
import X from 'lucide-vue-next/dist/esm/icons/x.js';
import { computed, inject, useId } from 'vue';
import { NxIconButton } from '../IconButton';
import { i18nInjectionKey } from '../internal';
import { splitHighlight, type NxHighlightSegment } from '../Highlight/highlight';
import type { TextQueryTerm } from '../lib/textQuery';
import NxFileTypeIcon from './NxFileTypeIcon.vue';
import { formatBytes, splitFileName } from './fileKind';

const props = withDefaults(
  defineProps<{
    name: string;
    look?: 'chip' | 'inline';
    /** Size in bytes, shown as the meta line unless `meta` is given. */
    size?: number | null;
    /** MIME type, for the icon. */
    type?: string;
    /** Replaces the size in the meta line. */
    meta?: string;
    /** Image URL shown instead of the type icon. */
    thumbnail?: string;
    /** `uploading` shows `progress`, `error` shows `error`. */
    state?: 'idle' | 'uploading' | 'error';
    /** Upload progress 0–1; without it the bar runs indeterminate. */
    progress?: number | null;
    error?: string;
    removable?: boolean;
    removeLabel?: string;
    openable?: boolean;
    disabled?: boolean;
    /** The chip that is open next to it, e.g. in a preview. */
    selected?: boolean;
    /** Gone: struck through, cannot be opened or removed. */
    deleted?: boolean;
    /** Why a deleted chip cannot be opened — tooltip and screen readers. */
    deletedHint?: string;
    /** Head row toggles the `contents` slot (an archive's files). */
    expandable?: boolean;
    locale?: string;
    /**
     * Longest the NAME may grow before its middle is cut, as a CSS length (`"45ch"`): the
     * icon, gaps, padding and ✕ come on top. Without it the name takes what the chip has.
     */
    nameMaxWidth?: string;
    /** Not sent yet (a file waiting to go out): drawn with a dashed border. */
    pending?: boolean;
    /**
     * Search to mark in the name, as `NxHighlight`'s `query`: a string, a list of
     * strings, or the terms of a text query. Default none.
     */
    query?: string | readonly string[] | readonly TextQueryTerm[] | null;
    /** Terms shorter than this are not marked (a string or a list of strings). Default 1. */
    minLength?: number;
    /** „mull“ also marks „Müll“ — part of the `accents` fold. Default `true`. */
    ignoreDiacritics?: boolean;
    /**
     * `accents`: case and accents fall away (`ü` is `u`). `spelled`: the fold of
     * the search, `ü` is `ue` and `ß` is `ss`, with exactly its ranges. Default `accents`.
     */
    fold?: 'accents' | 'spelled';
    /** At most this many terms mark (terms that cannot mark do not count). Default 10. */
    maxTerms?: number;
  }>(),
  {
    size: null,
    look: 'chip',
    type: undefined,
    meta: undefined,
    thumbnail: undefined,
    state: 'idle',
    progress: null,
    error: undefined,
    removable: false,
    removeLabel: 'Entfernen',
    openable: false,
    disabled: false,
    selected: false,
    deleted: false,
    deletedHint: 'Gelöscht',
    expandable: false,
    locale: undefined,
    nameMaxWidth: undefined,
    pending: false,
    query: null,
    minLength: 1,
    ignoreDiacritics: true,
    fold: 'accents',
    maxTerms: 10,
  }
);

const expanded = defineModel<boolean>('expanded', { default: false });

const emit = defineEmits<{
  open: [];
  remove: [];
}>();

const i18n = inject(i18nInjectionKey, null);
const locale = computed(() => props.locale ?? i18n?.locale?.value);
const contentsId = useId();

/** Drawn on one line like `nowrap` did: a folded MIME name loses its line break. */
const nameParts = computed(() => splitFileName(props.name.replace(/\s+/g, ' ').trim()));
/** The tail without its extension: the stem's end gives way before the extension does. */
const nameStem = computed(() =>
  nameParts.value.tail.slice(0, nameParts.value.tail.length - nameParts.value.ext.length)
);
/** The search hits of the whole drawn name; null without a hit, so the name draws as text. */
const nameHits = computed<NxHighlightSegment[] | null>(() => {
  if (!props.query || (Array.isArray(props.query) && props.query.length === 0)) return null;
  const { head, tail } = nameParts.value;
  const segments = splitHighlight(head + tail, props.query, {
    minLength: props.minLength,
    ignoreDiacritics: props.ignoreDiacritics,
    fold: props.fold,
    maxTerms: props.maxTerms,
  });
  return segments.some((segment) => segment.hit) ? segments : null;
});

/** The hit segments of the drawn name between two offsets (head, stem, extension). */
function hitsBetween(from: number, to: number): NxHighlightSegment[] {
  const out: NxHighlightSegment[] = [];
  let at = 0;
  for (const segment of nameHits.value ?? []) {
    const end = at + segment.text.length;
    const text = segment.text.slice(Math.max(0, from - at), Math.max(0, to - at));
    if (end > from && at < to && text) out.push({ text, hit: segment.hit });
    at = end;
  }
  return out;
}
const headHits = computed(() => hitsBetween(0, nameParts.value.head.length));
const stemHits = computed(() => {
  const from = nameParts.value.head.length;
  return hitsBetween(from, from + nameStem.value.length);
});
const extHits = computed(() => {
  const from = nameParts.value.head.length + nameStem.value.length;
  return hitsBetween(from, from + nameParts.value.ext.length);
});

/** A head of 5+ characters keeps a minimum width (see the CSS); a shorter one does not. */
const headHasFloor = computed(() => Array.from(nameParts.value.head).length >= 5);
const tooltip = computed(() =>
  props.deleted ? `${props.name}\n${props.deletedHint}` : props.name
);
/** Click and keys do something at all: not disabled, not deleted. */
const live = computed(() => !props.disabled && !props.deleted);
const openable = computed(() => props.openable && !props.expandable && live.value);
const toggleable = computed(() => props.expandable && live.value);
const canRemove = computed(() => props.removable && !props.deleted);
/** Shown open: a disabled or deleted archive stays shut whatever `expanded` says. */
const open = computed(() => props.expandable && expanded.value && live.value);

/** A button (disabled ones too) unless deleted: a deleted file does nothing. */
const asButton = computed(() => (props.expandable || props.openable) && !props.deleted);
/** Role, focus and state go on the head row when expandable, else on the chip. */
const interactiveAttrs = computed(() => ({
  role: asButton.value ? 'button' : undefined,
  tabindex: openable.value || toggleable.value ? 0 : undefined,
  'aria-disabled': props.disabled || undefined,
  'aria-current': props.selected ? ('true' as const) : undefined,
  title: tooltip.value,
}));
const rootAttrs = computed(() => (props.expandable ? {} : interactiveAttrs.value));
const headAttrs = computed(() => {
  if (!props.expandable) return {};
  if (!asButton.value) return interactiveAttrs.value;
  return {
    ...interactiveAttrs.value,
    'aria-expanded': open.value,
    'aria-controls': contentsId,
  };
});

const metaText = computed(() => {
  if (props.state === 'error' && props.error) return props.error;
  if (props.meta) return props.meta;
  if (typeof props.size === 'number') return formatBytes(props.size, locale.value);
  return '';
});
const determinate = computed(
  () => typeof props.progress === 'number' && Number.isFinite(props.progress)
);
const progressPct = computed(() =>
  determinate.value ? Math.max(0, Math.min(1, props.progress as number)) * 100 : 0
);

/** Enter or Space on the element itself, not on a button inside it. */
function isActivationKey(event: KeyboardEvent): boolean {
  if (event.target !== event.currentTarget) return false;
  if (event.key !== 'Enter' && event.key !== ' ') return false;
  event.preventDefault();
  return true;
}
/** Clicks on the inner buttons are theirs, not the chip's. */
function isOwnClick(event: MouseEvent): boolean {
  return !(event.target as HTMLElement).closest('button');
}

function onKeydown(event: KeyboardEvent) {
  if (openable.value && isActivationKey(event)) emit('open');
}
function onClick(event: MouseEvent) {
  if (openable.value && isOwnClick(event)) emit('open');
}
function onHeadKeydown(event: KeyboardEvent) {
  if (toggleable.value && isActivationKey(event)) expanded.value = !expanded.value;
}
function onHeadClick(event: MouseEvent) {
  if (toggleable.value && isOwnClick(event)) expanded.value = !expanded.value;
}
</script>

<template>
  <div
    class="nx-attachment-chip"
    :class="[
      `state-${props.state}`,
      {
        'look-inline': props.look === 'inline',
        'is-openable': openable,
        'is-toggleable': toggleable,
        'is-expandable': props.expandable,
        'is-expanded': open,
        'is-selected': props.selected,
        'is-deleted': props.deleted,
        'is-disabled': props.disabled,
        'is-pending': props.pending,
      },
    ]"
    v-bind="rootAttrs"
    data-testid="nx-attachment-chip"
    @click="onClick"
    @keydown="onKeydown"
  >
    <!-- Without `expandable` the head is `display: contents`: no box, no role. -->
    <div
      class="nx-attachment-chip__head"
      v-bind="headAttrs"
      @click="onHeadClick"
      @keydown="onHeadKeydown"
    >
      <img
        v-if="props.thumbnail"
        class="nx-attachment-chip__thumb"
        :src="props.thumbnail"
        alt=""
        loading="lazy"
      />
      <NxFileTypeIcon
        v-else
        :name="props.name"
        :type="props.type"
        :size="props.look === 'inline' ? 'sm' : 'md'"
      />
      <span class="nx-attachment-chip__text">
        <!-- Browsers read the two halves as two words: the whole name is the
             hidden text, the halves are only drawn. -->
        <span
          class="nx-attachment-chip__name"
          :style="props.nameMaxWidth ? { maxWidth: props.nameMaxWidth } : undefined"
        >
          <span
            class="nx-attachment-chip__sr-only"
            data-testid="nx-attachment-chip-name"
            v-text="props.name"
          />
          <template v-if="nameHits">
            <span
              class="nx-attachment-chip__name-head"
              :class="{ 'has-floor': headHasFloor }"
              aria-hidden="true"
            >
              <template v-for="(part, i) in headHits" :key="i">
                <mark v-if="part.hit" class="nx-attachment-chip__mark">{{ part.text }}</mark>
                <template v-else>{{ part.text }}</template>
              </template>
            </span>
            <span class="nx-attachment-chip__name-tail" aria-hidden="true">
              <span class="nx-attachment-chip__name-stem">
                <template v-for="(part, i) in stemHits" :key="i">
                  <mark v-if="part.hit" class="nx-attachment-chip__mark">{{ part.text }}</mark>
                  <template v-else>{{ part.text }}</template>
                </template>
              </span>
              <span class="nx-attachment-chip__name-ext">
                <template v-for="(part, i) in extHits" :key="i">
                  <mark v-if="part.hit" class="nx-attachment-chip__mark">{{ part.text }}</mark>
                  <template v-else>{{ part.text }}</template>
                </template>
              </span>
            </span>
          </template>
          <template v-else>
            <span
              class="nx-attachment-chip__name-head"
              :class="{ 'has-floor': headHasFloor }"
              aria-hidden="true"
              v-text="nameParts.head"
            />
            <span class="nx-attachment-chip__name-tail" aria-hidden="true">
              <span class="nx-attachment-chip__name-stem" v-text="nameStem" />
              <span class="nx-attachment-chip__name-ext" v-text="nameParts.ext" />
            </span>
          </template>
        </span>
        <span
          v-if="metaText"
          class="nx-attachment-chip__meta"
          :role="props.state === 'error' ? 'alert' : undefined"
        >
          {{ metaText }}
        </span>
        <span v-if="$slots.markers" class="nx-attachment-chip__markers">
          <slot name="markers" />
        </span>
        <span v-if="props.deleted" class="nx-attachment-chip__sr-only nx-attachment-chip__hint">
          {{ props.deletedHint }}
        </span>
      </span>
      <ChevronRightIcon
        v-if="props.expandable"
        filled
        class="nx-attachment-chip__chevron nxt-icon-sm"
        aria-hidden="true"
      />
      <span v-if="$slots.actions || canRemove" class="nx-attachment-chip__actions">
        <slot name="actions" />
        <NxIconButton
          v-if="canRemove"
          height="xs"
          :title="props.removeLabel"
          :aria-label="`${props.removeLabel}: ${props.name}`"
          :disabled="props.disabled"
          data-testid="nx-attachment-chip-remove"
          @click="emit('remove')"
        >
          <X class="nxt-icon-sm" aria-hidden="true" />
        </NxIconButton>
      </span>
    </div>
    <div
      v-if="props.expandable"
      v-show="open"
      :id="contentsId"
      class="nx-attachment-chip__contents"
      role="group"
      :aria-label="props.name"
    >
      <slot name="contents" />
    </div>
    <span
      v-if="props.state === 'uploading'"
      class="nx-attachment-chip__progress"
      :class="{ 'is-indeterminate': !determinate }"
      role="progressbar"
      :aria-valuenow="determinate ? Math.round(progressPct) : undefined"
      aria-valuemin="0"
      aria-valuemax="100"
      :aria-label="props.name"
    >
      <span
        class="nx-attachment-chip__bar"
        :style="{ width: determinate ? `${progressPct}%` : undefined }"
      />
    </span>
  </div>
</template>

<style scoped>
.nx-attachment-chip {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: var(--nxt-space-sm);
  min-width: 0;
  max-width: 100%;
  padding: var(--nxt-space-xs);
  overflow: hidden;
  border: var(--nxt-hairline) solid var(--nxt-border);
  border-radius: var(--nxt-radius-lg);
  background: var(--nxt-surface-raised-bg);
  box-shadow: var(--nxt-shadow-sm);
  transition:
    border-color var(--nxt-motion-fast) var(--nxt-ease-out),
    box-shadow var(--nxt-motion-fast) var(--nxt-ease-out);
}
.nx-attachment-chip.look-inline {
  gap: var(--nxt-space-xs);
  padding: 0;
  border: 0;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
}
.nx-attachment-chip__markers {
  display: flex;
  flex-wrap: wrap;
  gap: var(--nxt-space-2xs);
  margin-block-start: var(--nxt-space-2xs);
}
/* A file that has not gone out yet: the same border, dashed. */
.nx-attachment-chip.is-pending {
  border-style: dashed;
}
/* Sub-chips sit flat inside their archive. Kept above hover, focus and error:
   same specificity, so those still draw their shadows on a sub-chip. */
.nx-attachment-chip__contents > .nx-attachment-chip {
  box-shadow: none;
}
.nx-attachment-chip.is-openable {
  cursor: pointer;
}
.nx-attachment-chip.is-openable:hover {
  border-color: var(--nxt-border-strong);
  box-shadow: var(--nxt-shadow-md);
}
.nx-attachment-chip.is-openable.is-selected:hover {
  border-color: var(--nxt-brand-bg);
}
.nx-attachment-chip:focus-visible {
  outline: none;
  box-shadow: 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-focus-ring);
}
.nx-attachment-chip.state-error {
  border-color: var(--nxt-danger-border);
  box-shadow: inset calc(var(--nxt-hairline) * 3) 0 0 var(--nxt-danger-bg);
}
.nx-attachment-chip.is-disabled {
  opacity: 0.6;
}
.nx-attachment-chip.is-selected {
  border-color: var(--nxt-brand-bg);
  background: var(--nxt-brand-subtle-bg);
}
/* A plain chip's head is no box: its parts sit in the chip's own row. */
.nx-attachment-chip__head {
  display: contents;
}
/* Expandable: head row on top, the contents grow the chip downwards. */
.nx-attachment-chip.is-expandable {
  flex-direction: column;
  align-items: stretch;
  gap: 0;
}
.nx-attachment-chip.is-expandable > .nx-attachment-chip__head {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-sm);
  min-width: 0;
  border-radius: var(--nxt-radius-md);
}
.nx-attachment-chip.is-toggleable > .nx-attachment-chip__head {
  cursor: pointer;
}
.nx-attachment-chip.is-toggleable:has(> .nx-attachment-chip__head:hover) {
  border-color: var(--nxt-border-strong);
  box-shadow: var(--nxt-shadow-md);
}
.nx-attachment-chip.is-toggleable.is-selected:has(> .nx-attachment-chip__head:hover) {
  border-color: var(--nxt-brand-bg);
}
.nx-attachment-chip__head:focus-visible {
  outline: none;
  box-shadow: 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-focus-ring);
}
/* One glyph turned a quarter, as in NxDisclosure: ▸ closed, ▾ open. */
.nx-attachment-chip__chevron {
  flex: 0 0 auto;
  color: var(--nxt-fg-muted);
  transition: transform var(--nxt-motion-fast) var(--nxt-ease-out);
}
.nx-attachment-chip.is-expanded > .nx-attachment-chip__head > .nx-attachment-chip__chevron {
  transform: rotate(90deg);
}
.nx-attachment-chip__contents {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-2xs);
  margin-block-start: var(--nxt-space-xs);
  padding-block-start: var(--nxt-space-xs);
  border-block-start: var(--nxt-hairline) solid var(--nxt-border);
}
.nx-attachment-chip__thumb {
  flex: 0 0 auto;
  width: var(--nxt-control-size-md);
  height: var(--nxt-control-size-md);
  border-radius: var(--nxt-radius-md);
  object-fit: cover;
  background: var(--nxt-surface-sunken-bg);
}
.nx-attachment-chip__text {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-width: 0;
  line-height: var(--nxt-line-height-tight);
}
/* Cut in the middle: the head shrinks to an ellipsis first, the tail (end of
   the name + extension) only once the head is at its minimum. `pre` keeps a
   space at the seam that `nowrap` would drop at the end of the head. */
.nx-attachment-chip__name {
  display: flex;
  min-width: 0;
  overflow: hidden;
  color: var(--nxt-fg);
  font-size: var(--nxt-font-size-sm);
  font-weight: var(--nxt-font-weight-medium);
  white-space: pre;
}
/* Only drawn: a copy takes the hidden full name, once. */
.nx-attachment-chip__name-head,
.nx-attachment-chip__name-tail {
  user-select: none;
}
/* The high shrink factor makes the head give way first. Below 2em Chrome draws
   a first letter without the „…“, so a long head keeps that much room. A floor
   wider than a short head would leave a gap inside the name; a head of 3–4
   letters only belongs to a stem of 6–8 characters, which needs cutting below
   about 85px of text — narrower than any tile. */
.nx-attachment-chip__name-head {
  flex: 0 1000 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}
.nx-attachment-chip__name-head.has-floor {
  min-width: 2em;
}
.nx-attachment-chip__name-tail {
  display: flex;
  flex: 0 1 auto;
  min-width: 0;
}
/* Once the head is at its minimum the end of the stem gives way from its
   start, the extension stays. */
.nx-attachment-chip__name-stem {
  display: flex;
  flex: 0 1 auto;
  justify-content: flex-end;
  min-width: 0;
  overflow: hidden;
}
.nx-attachment-chip__name-ext {
  flex: 0 0 auto;
}
/* A search hit in the name, drawn like NxHighlight's mark. */
.nx-attachment-chip__mark {
  padding: 0;
  border-radius: calc(var(--nxt-hairline) * 2);
  background: color-mix(in srgb, var(--nxt-warning-bg) 38%, transparent);
  box-shadow: 0 0 0 calc(var(--nxt-hairline) * 1.5)
    color-mix(in srgb, var(--nxt-warning-bg) 38%, transparent);
  color: inherit;
  font-weight: var(--nxt-font-weight-semibold);
}
@media (forced-colors: active) {
  .nx-attachment-chip__mark {
    background: Mark;
    color: MarkText;
  }
}
.nx-attachment-chip.is-deleted > .nx-attachment-chip__head .nx-attachment-chip__name {
  color: var(--nxt-fg-muted);
}
.nx-attachment-chip.is-deleted > .nx-attachment-chip__head .nx-attachment-chip__name-head,
.nx-attachment-chip.is-deleted > .nx-attachment-chip__head .nx-attachment-chip__name-stem,
.nx-attachment-chip.is-deleted > .nx-attachment-chip__head .nx-attachment-chip__name-ext {
  text-decoration: line-through;
}
.nx-attachment-chip__sr-only {
  position: absolute;
  width: var(--nxt-hairline);
  height: var(--nxt-hairline);
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}
.nx-attachment-chip__meta {
  overflow: hidden;
  margin-block-start: var(--nxt-space-2xs);
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-xs);
  font-variant-numeric: tabular-nums;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.nx-attachment-chip.state-error > .nx-attachment-chip__head .nx-attachment-chip__meta {
  color: var(--nxt-danger-subtle-fg);
}
.nx-attachment-chip__actions {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  gap: var(--nxt-space-2xs);
}
.nx-attachment-chip__progress {
  position: absolute;
  inset-inline: 0;
  inset-block-end: 0;
  height: calc(var(--nxt-hairline) * 3);
  overflow: hidden;
  background: var(--nxt-chart-track);
}
.nx-attachment-chip__bar {
  display: block;
  height: 100%;
  background: var(--nxt-brand-bg);
  transition: width var(--nxt-motion-base) var(--nxt-ease-out);
}
.nx-attachment-chip__progress.is-indeterminate .nx-attachment-chip__bar {
  width: 35%;
  animation: nx-attachment-slide 1.2s var(--nxt-ease-in-out) infinite;
}
@keyframes nx-attachment-slide {
  from {
    transform: translateX(-100%);
  }
  to {
    transform: translateX(300%);
  }
}
@media (prefers-reduced-motion: reduce) {
  .nx-attachment-chip,
  .nx-attachment-chip__chevron,
  .nx-attachment-chip__bar {
    transition: none;
  }
  .nx-attachment-chip__progress.is-indeterminate .nx-attachment-chip__bar {
    width: 100%;
    animation: none;
    opacity: 0.5;
  }
}
/* The same where a subtree asks for less motion: data-nx-motion="reduced"
   on an ancestor (reset.css does the durations). */
:where([data-nx-motion='reduced']) .nx-attachment-chip,
:where([data-nx-motion='reduced']) .nx-attachment-chip__chevron,
:where([data-nx-motion='reduced']) .nx-attachment-chip__bar {
  transition: none;
}
:where([data-nx-motion='reduced'])
  .nx-attachment-chip__progress.is-indeterminate
  .nx-attachment-chip__bar {
  width: 100%;
  animation: none;
  opacity: 0.5;
}
</style>
